import type { ReactNode, SVGProps } from "react";

/**
 * The dashboard's glyphs, drawn from the prototype's `ic()` set (admin.js /
 * register.js / l3.js): 24-box, 1.8px stroke, round caps, currentColor.
 * Decorative — the control around them carries the accessible name.
 */
type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

function Glyph({ children, ...props }: P & { children: ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			focusable={false}
			{...props}
		>
			{children}
		</svg>
	);
}

export const ShieldIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 3.5 5 6.5v5c0 4.4 3 7.8 7 9 4-1.2 7-4.6 7-9v-5Z" />
		<path d="m9 12 2.2 2.2L15.5 10" />
	</Glyph>
);
export const CardIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="3" y="5.5" width="18" height="13" rx="2.2" />
		<circle cx="8.5" cy="11" r="2" />
		<path d="M6 15.5c.5-1.3 1.4-2 2.5-2s2 .7 2.5 2M13.5 10h4.5M13.5 13h3" />
	</Glyph>
);
export const CheckIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m5 12.5 4.5 4.5L19 7.5" />
	</Glyph>
);
export const InboxIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4 13.5 6.5 5h11l2.5 8.5V19a.5.5 0 0 1-.5.5h-15A.5.5 0 0 1 4 19Z" />
		<path d="M4 13.5h4.5l1 2h5l1-2H20" />
	</Glyph>
);
export const WarnIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 4 2.8 19.5h18.4Z" />
		<path d="M12 10v4.2M12 17v.2" />
	</Glyph>
);
export const UsersIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="9" cy="8.5" r="3.2" />
		<path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
		<circle cx="16.5" cy="9" r="2.6" />
		<path d="M15.5 14.2c2.6-.3 4.6 1.3 5 4.3" />
	</Glyph>
);
export const GlobeIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<path d="M3.5 12h17M12 3.5c2.3 2.4 3.4 5.2 3.4 8.5s-1.1 6.1-3.4 8.5c-2.3-2.4-3.4-5.2-3.4-8.5s1.1-6.1 3.4-8.5Z" />
	</Glyph>
);
export const InfoIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<path d="M12 11v5M12 7.6v.2" />
	</Glyph>
);
export const RightIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m9 6 6 6-6 6" />
	</Glyph>
);
export const DownIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m6 9 6 6 6-6" />
	</Glyph>
);
export const EditIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M5 19h3.5L18.2 9.3a2 2 0 0 0 0-2.8l-.7-.7a2 2 0 0 0-2.8 0L5 15.5Z" />
		<path d="m13.5 7 3.5 3.5" />
	</Glyph>
);
export const NoPhotoIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="8.5" r="3.6" />
		<path d="M5 20c.8-3.6 3.6-5.6 7-5.6s6.2 2 7 5.6" />
		<path d="M4 4l16 16" />
	</Glyph>
);
export const UserPlusIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="10" cy="8.5" r="3.6" />
		<path d="M3.5 20c.8-3.6 3.4-5.6 6.5-5.6 1.4 0 2.7.4 3.8 1.2" />
		<path d="M18 13.5v6M15 16.5h6" />
	</Glyph>
);
export const ShortlistIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="5.5" y="4.5" width="13" height="16" rx="2" />
		<path d="M9 4.5v-1h6v1" />
		<path d="m9 13 2 2 4-4" />
	</Glyph>
);
export const ListIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M9 7h11M9 12h11M9 17h11M4.5 7h.01M4.5 12h.01M4.5 17h.01" />
	</Glyph>
);
export const FilterIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4 6.5h16M7 12h10M10 17.5h4" />
	</Glyph>
);
export const CloseIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M6 6l12 12M18 6 6 18" />
	</Glyph>
);
export const LayersIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m12 4 8.5 4.5L12 13 3.5 8.5Z" />
		<path d="m3.5 12.5 8.5 4.5 8.5-4.5" />
	</Glyph>
);
